import React, { useEffect, useState } from "react";
import {
    Card, Typography, Form, Input, Select, DatePicker, Button,
    Row, Col, Space, Upload, message, Tag,
} from "antd";
import { InboxOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { RequirementService } from "../../../Services/Manage/RequirementService";
import { UserService } from "../../../Services/Manage/UserService";
import { ENDPOINT } from "../../../Services/ENDPOINT";

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

// Exact same CardTitle pattern as CreateEnquiry.jsx — no icon, "no. text" format
const CardTitle = ({ no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

// const categoryOptions = [
//     { value: "Authentication", label: "Authentication" },
//     { value: "Product", label: "Product" },
//     { value: "Order Management", label: "Order Management" },
//     { value: "Payment", label: "Payment" },
//     { value: "Notification", label: "Notification" },
//     { value: "General", label: "General" },
// ];

// const requirementTypeOptions = [
//     { value: "Functional", label: "Functional" },
//     { value: "Non-Functional", label: "Non-Functional" },
//     { value: "UI/UX", label: "UI/UX" },
//     { value: "Technical", label: "Technical" },
//     { value: "Business", label: "Business" },
// ];

const priorityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const statusOptions = [
    { value: "Draft", label: "Draft" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Change Request", label: "Change Request" },
    { value: "Rejected", label: "Rejected" },
];

const managerStatusOptions = [
    { value: "Approved", label: "Approved" },
    { value: "Change Request", label: "Change Request" },
    { value: "Rejected", label: "Rejected" },
];



// const moduleOptions = [
//     { value: "Authentication", label: "Authentication" },
//     { value: "Catalog", label: "Catalog" },
//     { value: "Checkout", label: "Checkout" },
//     { value: "Admin Panel", label: "Admin Panel" },
// ];

// const affectedAreaOptions = [
//     { value: "Frontend", label: "Frontend" },
//     { value: "Backend", label: "Backend" },
//     { value: "Database", label: "Database" },
//     { value: "API", label: "API" },
// ];

const effortOptions = [
    { value: "Small (1-2 days)", label: "Small (1-2 days)" },
    { value: "Medium (3-5 days)", label: "Medium (3-5 days)" },
    { value: "Large (1-2 weeks)", label: "Large (1-2 weeks)" },
    { value: "X-Large (2+ weeks)", label: "X-Large (2+ weeks)" },
];



const AddRequirement = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();


    const [moduleOptions, setModuleOptions] = useState([]);
    const [affectedAreaOptions, setAffectedAreaOptions] = useState([]);

    const projectId = location.state?.id || null;       // numeric pro_id (Projects table PK)
    const projectName = location.state?.pro_name || "-";
    const projectNo = location.state?.pro_no || "-";

    const managerId = location.state?.pro_manager_id || null;
    const perm = getPermission("/overview-project");
    const viewAll = perm.view_all === true;

    const [isProjectManager, setIsProjectManager] = useState(false);
    const canChooseRequestedBy = viewAll || isProjectManager;

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [requirementTypeOptions, setRequirementTypeOptions] = useState([]);

    // Edit / View mode support
    const reqDbId = location.state?.reqDbId || null;    // numeric requirements.id (row PK) — presence = edit/view mode
    const action = location.state?.action;              // 'Edit' | 'View'
    const enableEdit = location.state?.enableEdit ?? true;
    const isEditMode = !!reqDbId;
    const readOnly = isEditMode && !enableEdit;

    const [fileList, setFileList] = useState([]);
    const [reqId, setReqId] = useState("REQ-001");
    const [existingRecord, setExistingRecord] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);

    const [requestedByOptions, setRequestedByOptions] = useState([]);

    const [dependencyOptions, setDependencyOptions] = useState([]);

    const summary = Form.useWatch([], form) || {};

    useEffect(() => {
        if (isEditMode) {
            getRequirementById(reqDbId);
        } else {
            getLastRequirement();
        }
    }, [reqDbId]);

    useEffect(() => {
        getRequestedByOptions();
    }, []);


    useEffect(() => {
        getDependencyOptions();
    }, []);

    useEffect(() => {
        getDropdownValues("category", setCategoryOptions);
        getDropdownValues("requirement_type", setRequirementTypeOptions);
        getDropdownValues("module", setModuleOptions);
        getDropdownValues("affected_area", setAffectedAreaOptions);
    }, []);

    useEffect(() => {
        resolveCurrentUser();
    }, []);

    const resolveCurrentUser = async () => {
        try {
            const user = await UserService.getOmeUser();
            const uid = String(user?.uid ?? "").trim();
            const managerMatch = String(managerId ?? "").trim() === uid;
            setIsProjectManager(managerMatch);

            if (!isEditMode && !viewAll && !managerMatch && user?.name) {
                form.setFieldsValue({ requested_by: user.name });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getDropdownValues = async (field, setter) => {
        try {
            const request = await RequirementService.getDropdownValues(field);
            if (request?.status) {
                setter((request.data || []).map((v) => ({ value: v, label: v })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getDependencyOptions = async () => {
        try {
            const request = projectId
                ? await RequirementService.getAllByProject(projectId)
                : await RequirementService.getAllRequirement();

            if (request?.status) {
                const rows = (request.data || [])
                    .filter((r) => r.id !== reqDbId)
                    .map((r) => ({
                        value: r.req_id,
                        label: `${r.req_id} - ${r.requirement_title}`,
                    }));
                setDependencyOptions(rows);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getRequestedByOptions = async () => {
        try {
            let engineerIds = location.state?.pro_engineer;
            if (typeof engineerIds === "string") {
                try { engineerIds = JSON.parse(engineerIds); } catch { engineerIds = []; }
            }

            const allowedIds = new Set(
                (engineerIds || [])
                    .filter(Boolean)
                    .map((id) => String(id).trim())
            );

            const request = await UserService.getAllUsers();
            const users = request?.data || [];

            const relevant = users.filter((u) => allowedIds.has(String(u.user_id).trim()));

            setRequestedByOptions(
                relevant.map((u) => ({
                    value: u.name,
                    label: `${u.name}${u.user_id ? ` (${u.user_id})` : ""}`,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };

    const getLastRequirement = async () => {
        try {
            const request = await RequirementService.getLastRequirement();
            let nextNo = "001";
            if (request?.status && request?.data?.req_id) {
                const seqPart = request.data.req_id.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setReqId(`REQ-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getRequirementById = async (id) => {
        try {
            setLoadingRecord(true);
            const request = await RequirementService.getRequirementById(id);
            if (request?.status && request?.data) {
                const data = request.data;
                setExistingRecord(data);
                setReqId(data.req_id || "REQ-001");

                let attachments = data.attachments;
                if (typeof attachments === "string") {
                    try { attachments = JSON.parse(attachments || "[]"); } catch { attachments = []; }
                }
                setFileList(
                    (attachments || []).map((f, idx) => ({
                        uid: `existing-${idx}`,
                        name: f.filename || f.name,
                        status: "done",
                        url: f.path?.startsWith("http") ? f.path : `${ENDPOINT.base}/${f.path}`,
                        response: { file: f },
                    }))
                );

                form.setFieldsValue({
                    requirement_title: data.requirement_title,
                    category: data.category,
                    requirement_type: data.requirement_type,
                    priority: data.priority,
                    status: data.status,
                    requested_by: data.requested_by,
                    description: data.description,
                    business_need: data.business_need,
                    acceptance_criteria: data.acceptance_criteria,
                    module: data.module ? [data.module] : [],
                    affected_area: data.affected_area ? [data.affected_area] : [],
                    estimated_effort: data.estimated_effort,
                    target_release: data.target_release ? dayjs(data.target_release) : null,
                    dependency: data.dependency,
                    related_requirement: data.related_requirement,
                    remarks: data.remarks,
                });
            } else {
                messageApi.error(request?.message || "Requirement not found");
            }
        } catch (error) {
            console.error("Error fetching requirement:", error);
            messageApi.error("Failed to load requirement");
        } finally {
            setLoadingRecord(false);
        }
    };

    const uploadAttachments = async () => {
        const uploadedFiles = [];
        for (const f of fileList) {
            if (!f.originFileObj) continue; // already-uploaded / existing file, skip
            try {
                const formData = new FormData();
                formData.append("file", f.originFileObj);
                const res = await fetch(`${ENDPOINT.Requirement}/upload-file`, {
                    method: "POST",
                    body: formData,
                });
                const result = await res.json();
                if (result.status && result.success) {
                    uploadedFiles.push(result.file);
                }
            } catch (e) {
                console.error("Attachment upload failed:", e);
            }
        }
        return uploadedFiles;
    };

    const buildPayload = async (values, isDraft) => {
        const user = await UserService.getOmeUser();

        // Merge already-saved attachments (edit mode) with any newly uploaded ones
        const newlyUploaded = await uploadAttachments();
        const existingAttachments = fileList
            .filter((f) => !f.originFileObj)
            .map((f) => f.response?.file)
            .filter(Boolean);
        const attachments = [...existingAttachments, ...newlyUploaded];

        return {
            requirement_title: values.requirement_title,
            category: Array.isArray(values.category) ? values.category[0] : values.category,
            requirement_type: Array.isArray(values.requirement_type) ? values.requirement_type[0] : values.requirement_type,
            priority: values.priority,
            status: isDraft
                ? "Draft"
                : (canChooseRequestedBy ? (values.status || "Draft") : "Pending"),
            requested_by: values.requested_by,
            description: values.description,
            business_need: values.business_need,
            acceptance_criteria: values.acceptance_criteria,
            module: Array.isArray(values.module) ? values.module[0] : values.module,
            affected_area: Array.isArray(values.affected_area) ? values.affected_area[0] : values.affected_area,
            estimated_effort: values.estimated_effort,
            target_release: values.target_release ? dayjs(values.target_release).format("YYYY-MM-DD") : null,
            dependency: values.dependency,
            related_requirement: values.related_requirement,
            remarks: values.remarks,
            req_id: reqId,
            pro_id: projectId,
            pro_no: projectNo,
            attachments: JSON.stringify(attachments),
            is_draft: isDraft,
            added_by: user?.uid,
        };
    };

    const submitRequirement = async (values, isDraft = false) => {
        try {
            isDraft ? setSavingDraft(true) : setSubmitting(true);
            const payload = await buildPayload(values, isDraft);

            const request = isEditMode
                ? await RequirementService.updateRequirement(payload, reqDbId)
                : await RequirementService.createRequirement(payload);

            if (request?.status) {
                messageApi.success(
                    request.message || (isDraft ? "Saved as draft" : isEditMode ? "Requirement updated" : "Requirement submitted")
                );
                navigate(-1);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error submitting requirement:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSavingDraft(false);
            setSubmitting(false);
        }
    };

    const onFinish = (values) => {
        submitRequirement(values, false);
    };

    const handleSaveDraft = () => {
        const values = form.getFieldsValue();
        submitRequirement(values, true);
    };

    const headerLabel = isEditMode ? (readOnly ? "View Requirement" : "Edit Requirement") : "Add Requirement";

    return (
        <>
            <style>{compactFormStyle}</style>
            {contextHolder}

            <Card
                loading={loadingRecord}
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(-1)}

                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {headerLabel}
                                    <Tag color="blue" style={{ fontSize: "16px", padding: "6px 16px", borderRadius: "8px", fontWeight: 600 }}>
                                        {reqId}
                                    </Tag>
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    <Space>
                        {readOnly ? (
                            <Button
                                size="large"
                                onClick={() =>
                                    navigate("/requirement-gathering/add", {
                                        state: { ...location.state, action: "Edit", enableEdit: true },
                                    })
                                }
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}
                            >
                                Enable Edit
                            </Button>
                        ) : (
                            <>
                                <Button size="large" loading={savingDraft} onClick={handleSaveDraft}>
                                    Save as Draft
                                </Button>
                                <Button
                                    type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                                    style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                >
                                    {isEditMode ? "Update Requirement" : "Submit Requirement"}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                <Row gutter={16}>
                    {/* Left: Form */}
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            disabled={readOnly}
                            initialValues={{ status: "Draft", priority: "High" }}
                        >
                            <Card title={<CardTitle no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Project">
                                            <Input size="large" value={projectName} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="requirement_title" label="Requirement Title" rules={[{ required: true, message: "Please enter requirement title" }]}>
                                            <Input size="large" placeholder="Enter requirement title" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Requirement ID">
                                            <Input size="large" readOnly value={reqId} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="category" label="Category" rules={[{ required: true, message: "Please select category" }]}>
                                            <Select
                                                options={categoryOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type new category"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="requirement_type" label="Requirement Type" rules={[{ required: true, message: "Please select requirement type" }]}>
                                            <Select
                                                options={requirementTypeOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type new requirement type"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Please select priority" }]}>
                                            <Select options={priorityOptions} size="large" placeholder="Select priority" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="status" label="Status">
                                            {canChooseRequestedBy ? (
                                                <Select options={managerStatusOptions} size="large" placeholder="Select status" />
                                            ) : (
                                                <Input size="large" readOnly />
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="requested_by" label="Requested By" rules={[{ required: true, message: "Please select requested by" }]}>
                                            {canChooseRequestedBy ? (
                                                <Select options={requestedByOptions} size="large" showSearch allowClear placeholder="Select team member" />
                                            ) : (
                                                <Input size="large" readOnly />
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={2} text="Requirement Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={14}>
                                        <Form.Item name="description" label="Detailed Description" rules={[{ required: true, message: "Please enter description" }]}>
                                            <TextArea rows={7} size="large" maxLength={5000} showCount placeholder="Enter detailed description of the requirement..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Form.Item name="business_need" label="Business Need / Objective">
                                            <TextArea rows={3} size="large" maxLength={1000} showCount placeholder="Enter business need or objective..." />
                                        </Form.Item>
                                        <Form.Item name="acceptance_criteria" label="Acceptance Criteria">
                                            <TextArea rows={3} size="large" maxLength={1000} showCount placeholder="Enter acceptance criteria..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={3} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="module" label="Module / Section">
                                            <Select
                                                options={moduleOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type module / section"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="affected_area" label="Affected Area">
                                            <Select
                                                options={affectedAreaOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type affected area"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="estimated_effort" label="Estimated Effort">
                                            <Select options={effortOptions} size="large" showSearch allowClear placeholder="Select effort" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="target_release" label="Target Release">
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select target release" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="dependency" label="Dependency">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Select dependency" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="related_requirement" label="Related Requirement (if any)">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Search requirement..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                fileList={fileList}
                                                multiple
                                                disabled={readOnly}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                beforeUpload={(file) => {
                                                    setFileList((prev) => [...prev, { uid: `${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file }]);
                                                    return false;
                                                }}
                                                onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
                                                style={{ padding: "6px 0" }}
                                            >
                                                <p className="ant-upload-drag-icon" style={{ margin: "4px 0" }}><InboxOutlined /></p>
                                                <p className="ant-upload-text" style={{ fontSize: 12, margin: 0 }}>Click to upload or drag and drop</p>
                                                <p className="ant-upload-hint" style={{ fontSize: 11 }}>PNG, JPG, PDF, DOC, DOCX (Max. 10MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={2} size="large" maxLength={500} showCount placeholder="Enter remarks (optional)..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>
                    </Col>

                    {/* Right: Summary + Tips + Links — plain titles, no icons */}
                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Requirement Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Project", projectName],
                                    ["Requirement ID", reqId],
                                    ["Category", summary.category || "-"],
                                    ["Type", summary.requirement_type || "-"],
                                    ["Priority", summary.priority || "-"],
                                    ["Status", summary.status || "Draft"],
                                    ["Requested By", summary.requested_by || "-"],
                                    ["Added On", existingRecord?.created_at ? dayjs(existingRecord.created_at).format("DD-MM-YYYY") : dayjs().format("DD-MM-YYYY")],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Write clear and concise requirement title.</li>
                                <li>Provide complete details for better understanding.</li>
                                <li>Add acceptance criteria to define success.</li>
                                <li>Attach relevant documents or screenshots.</li>
                                <li>Set the correct priority and target release date.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {["View Project", "Requirement List", "Requirement Templates", "Import Requirement"].map((label) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(-1)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>

                {/* Footer buttons */}
                {!readOnly && (
                    <div style={{ textAlign: "right", marginTop: 16 }}>
                        <Button size="large" style={{ marginRight: 8 }} onClick={() => form.resetFields()}>
                            Reset
                        </Button>
                        <Button
                            size="large" loading={savingDraft} onClick={handleSaveDraft}
                            style={{ marginRight: 8, color: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Save as Draft
                        </Button>
                        <Button
                            type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            {isEditMode ? "Update Requirement" : "Submit Requirement"}
                        </Button>
                    </div>
                )}
            </Card>
        </>
    );
};

export default AddRequirement;